"use client";

/**
 * Reservation.tsx
 *
 * Online table reservation section for the Royal Blue homepage.
 *
 * Flow:
 *  1. User fills the form and submits → checkTablesAction is called
 *  2. If tables are available → TablePickerModal opens
 *  3. User picks a table → bookTableAction is called
 *  4. On success, a success message is shown and the form resets
 */

import React, { Suspense, useState } from "react";
import { useIsPreviewMode } from "@/hooks/useIsPreviewMode";
import { useAppSelector } from "@/store/hooks";
import { checkTablesAction } from "@/actions/serverActions/checkTablesAction";
import { bookTableAction } from "@/actions/serverActions/bookTableAction";
import {
  Users,
  Calendar as CalendarIcon,
  Clock,
  CheckCircle2,
  ShieldCheck,
  Zap,
} from "lucide-react";

import OpeningTimesBanner from "./OpeningTimesBanner";
import TablePickerModal from "./TablePickerModal";

// ---------------------------------------------------------------------------
// Defaults (used when CMS data is missing)
// ---------------------------------------------------------------------------

const DEFAULT_SCHEDULE = [
  { day: "Week days", time: "09.00 – 24:00" },
  { day: "Saturday", time: "08:00 – 03.00" },
  { day: "Sunday", time: "Day off" },
];

const INITIAL_FORM = {
  party_size: "2",
  reservation_date: "",
  start_time: "19:00",
  // Note: customer details are now collected in the booking modal step
  special_requests: "",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * The API can return errors as a DRF validation object or a plain string.
 * This always returns a readable message.
 */
function parseApiError(raw: unknown, fallback: string): string {
  if (!raw) return fallback;
  if (typeof raw === "object") {
    const firstKey = Object.keys(raw as object)[0];
    const val = (raw as any)[firstKey];
    return `${firstKey}: ${Array.isArray(val) ? val[0] : val}`;
  }
  return String(raw);
}

/**
 * The "check tables" endpoint can return the list of tables in several
 * different shapes. This finds the array regardless of nesting.
 */
function extractTables(data: unknown): any[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const d = data as Record<string, unknown>;
    if (Array.isArray(d.results)) return d.results as any[];
    if (Array.isArray(d.data)) return d.data as any[];
    if (Array.isArray(d.available_tables)) return d.available_tables as any[];
    // Last resort: look for any array value in the response
    for (const val of Object.values(d)) {
      if (Array.isArray(val)) return val as any[];
    }
  }
  return [];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Reservation = () => {
  const isPreview = useIsPreviewMode();
  const { data: content } = useAppSelector((state: any) => state.content);
  const data = content?.reservation;

  // CMS content with fallbacks
  const schedule = data?.openingTimes?.schedule?.length
    ? data.openingTimes.schedule
    : DEFAULT_SCHEDULE;

  const backgroundImage = data?.backgroundImage
    ? `linear-gradient(rgba(27,26,26,0.6), rgba(27,26,26,0.6)), url("${data.backgroundImage}")`
    : 'linear-gradient(rgba(27,26,26,0.6), rgba(27,26,26,0.6)), url("/themes/royal-blue/reservation-demo-bg.jpg")';

  // Form state
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [availableTables, setAvailableTables] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ── Step 1: Check available tables ───────────────────────────────────────
  const handleCheckTables = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPreview) return;

    if (!formData.reservation_date || !formData.start_time) {
      setMessage({
        text: "Please select a date and time.",
        type: "error",
      });
      return;
    }

    setIsLoading(true);
    setMessage({ text: "", type: "" });

    const res = await checkTablesAction({
      party_size: Number(formData.party_size),
      date: formData.reservation_date,
      start_time: formData.start_time,
    });

    setIsLoading(false);

    if (res.success && res.data) {
      const tables = extractTables(res.data);
      if (tables.length > 0) {
        setAvailableTables(tables);
        setIsModalOpen(true);
      } else {
        setMessage({
          text: "No tables available for the selected time.",
          type: "error",
        });
      }
    } else {
      setMessage({
        text: parseApiError(res.error, "Failed to check availability."),
        type: "error",
      });
    }
  };

  // ── Step 2: Book the chosen table ────────────────────────────────────────
  const handleBookTable = async (tableId: string, customerDetails: any) => {
    setIsLoading(true);
    setMessage({ text: "", type: "" });

    const res = await bookTableAction({
      ...customerDetails,
      party_size: Number(formData.party_size),
      reservation_date: formData.reservation_date,
      start_time: formData.start_time,
      table_id: tableId,
      special_requests:
        customerDetails.special_requests || formData.special_requests,
    });

    setIsLoading(false);

    if (res.success) {
      setMessage({
        text: "Your reservation request is pending! We will notify you once it is confirmed.",
        type: "success",
      });
      setIsModalOpen(false);
      setFormData(INITIAL_FORM);
    } else {
      setMessage({
        text: parseApiError(res.error, "Failed to complete reservation."),
        type: "error",
      });
      setIsModalOpen(false);
    }
  };

  return (
    <section
      id="reservation"
      className="relative py-[100px] bg-fixed bg-cover bg-center"
      style={{ backgroundImage }}
    >
      <div className="container mx-auto px-[15px] max-w-[1170px] relative z-10">
        <div className="bg-white p-[30px] lg:p-[70px] flex flex-col lg:flex-row shadow-[0_5px_83px_0_rgba(40,40,40,0.08)] rounded-[4px] justify-between items-stretch">
          {/* Left: opening hours + phone */}
          <OpeningTimesBanner
            subtitle={data?.openingTimes?.subtitle || "Check Availability"}
            title={data?.openingTimes?.title || "Opening Times"}
            schedule={schedule}
            callUsTitle={data?.openingTimes?.callUsTitle || "Call Us Now"}
            phoneNumber={data?.openingTimes?.phoneNumber || "0201023456789"}
          />

          {/* Right: reservation form */}
          <div className="lg:w-[57%] relative bg-white p-[20px] md:p-[40px_40px] m-0 border border-[#c59d5f] rounded-[2px]">
            {/* Inner gold border */}
            <div className="absolute -top-[4px] bottom-[0px] left-[2px] right-[2px] h-[calc(100%+8px)] border border-[#c59d5f] rounded-[2px] pointer-events-none z-0" />

            <div className="relative z-10 w-full">
              <p className="text-[#444] text-[14px] leading-relaxed mb-[15px] font-inter">
                {data?.reservationForm?.description ||
                  "You can book your table online easily in just a couple of minutes. We take reservations for lunch, just check the availability of your table."}
              </p>

              {/* Inline success / error message */}
              {message.text && (
                <div
                  className={`mb-4 p-3 rounded text-sm font-inter ${
                    message.type === "error"
                      ? "bg-red-50 text-red-600 border border-red-200"
                      : "bg-green-50 text-green-700 border border-green-200"
                  }`}
                >
                  {message.text}
                </div>
              )}

              <form className="mt-[30px]" onSubmit={handleCheckTables}>
                <div className="flex flex-wrap -mx-[15px]">
                  {/* Party size */}
                  <div className="w-full sm:w-1/2 px-[15px] mb-[30px]">
                    <div className="relative">
                      <select
                        name="party_size"
                        value={formData.party_size}
                        onChange={handleInputChange}
                        className="w-full h-[50px] bg-[#f6f6f6] border border-[#dbdbdb] rounded-[2px] px-[15px] text-[14px] text-[#222] font-inter appearance-none focus:border-[#c59d5f] focus:ring-0 transition-colors"
                      >
                        <option value="1">1 person</option>
                        <option value="2">2 people</option>
                        <option value="3">3 people</option>
                        <option value="4">4 people</option>
                        <option value="5">5 people</option>
                        <option value="6">6 people</option>
                        <option value="7">7+ people</option>
                      </select>
                      {/* Custom dropdown arrow */}
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-[#9b9b9b]">
                        <svg
                          className="fill-current h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="w-full sm:w-1/2 px-[15px] mb-[30px]">
                    <input
                      required
                      type="date"
                      name="reservation_date"
                      min={new Date().toISOString().split("T")[0]}
                      value={formData.reservation_date}
                      onChange={handleInputChange}
                      className="w-full h-[50px] bg-[#f6f6f6] border border-[#dbdbdb] rounded-[2px] px-[15px] text-[14px] text-[#222] font-inter focus:border-[#c59d5f] focus:ring-0 transition-colors"
                    />
                  </div>

                  {/* Time */}
                  <div className="w-full sm:w-1/2 px-[15px] mb-[30px]">
                    <input
                      required
                      type="time"
                      name="start_time"
                      value={formData.start_time}
                      onChange={handleInputChange}
                      className="w-full h-[50px] bg-[#f6f6f6] border border-[#dbdbdb] rounded-[2px] px-[15px] text-[14px] text-[#222] font-inter focus:border-[#c59d5f] focus:ring-0 transition-colors"
                    />
                  </div>

                  {/* Submit */}
                  <div className="w-full px-[15px]">
                    <button
                      type="submit"
                      disabled={isLoading || isPreview}
                      title={
                        isPreview
                          ? "Booking is disabled in preview mode"
                          : undefined
                      }
                      className="w-full h-[50px] bg-[#222] text-white font-bold uppercase tracking-[1.3px] text-[13px] rounded-[1px] hover:bg-[#c59d5f] hover:border-[#c59d5f] border-2 border-[#222] transition-all duration-300 shadow-[0_3px_7px_0_rgba(0,0,0,0.15)] flex justify-center items-center disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isLoading
                        ? "Checking Availability..."
                        : data?.reservationForm?.buttonLabel || "Find A Table"}
                      {!isLoading && (
                        <Zap className="ml-2 w-4 h-4 text-[#c59d5f] group-hover:text-white transition-colors" />
                      )}
                    </button>
                  </div>

                  {/* Trust Badges / Feature Highlights to fill space */}
                  <div className="w-full px-[15px] pt-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                      <div className="flex flex-col items-center text-center group">
                        <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center mb-3 group-hover:bg-[#c59d5f] group-hover:text-white transition-all">
                          <CheckCircle2
                            size={20}
                            className="text-[#c59d5f] group-hover:text-white"
                          />
                        </div>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-[#222]">
                          Instant
                          <br />
                          Confirmation
                        </p>
                      </div>
                      <div className="flex flex-col items-center text-center group">
                        <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center mb-3 group-hover:bg-[#c59d5f] group-hover:text-white transition-all">
                          <ShieldCheck
                            size={20}
                            className="text-[#c59d5f] group-hover:text-white"
                          />
                        </div>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-[#222]">
                          Secure
                          <br />
                          Booking
                        </p>
                      </div>
                      <div className="flex flex-col items-center text-center group">
                        <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center mb-3 group-hover:bg-[#c59d5f] group-hover:text-white transition-all">
                          <Zap
                            size={20}
                            className="text-[#c59d5f] group-hover:text-white"
                          />
                        </div>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-[#222]">
                          Best Seat
                          <br />
                          Selection
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Table picker modal (shown after API returns available tables) */}
      {isModalOpen && (
        <Suspense>
          <TablePickerModal
            tables={availableTables}
            isLoading={isLoading}
            onSelect={handleBookTable}
            onClose={() => setIsModalOpen(false)}
          />
        </Suspense>
      )}
    </section>
  );
};

export default Reservation;
